import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import {
  ASSET_CATEGORIES,
  DEBT_CATEGORIES,
  ACCOUNT_TYPES,
} from "@/shared/constants/estateCategories";
import type { EstateItem, CreateEstateItemRequest } from "@/shared/types/estateTypes";
import {
  getNameLabel,
  getTypeLabel,
  getValueLabel,
  getPlaceholder,
} from "@/shared/utils/estateItemLabels";
import { resolveBalance } from "@/shared/utils/balanceEstimates";
import { currencyInput } from "@/shared/utils/currencyInput";
import { BalanceEstimateSection } from "./BalanceEstimateSection";
import { enqueueSnackbar } from "notistack";

const VEHICLE_NAME_PATTERN = /^(\d{4})\s+(\S+)\s+(.+)$/;

function parseVehicleMetadata(name: string): Record<string, unknown> | undefined {
  const match = name.match(VEHICLE_NAME_PATTERN);
  if (!match) return undefined;
  const [, year, make, model] = match;
  return { year: parseInt(year), make, model };
}

interface EditEstateItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  estateId: number;
  item: EstateItem;
}

const allCategories = [...ASSET_CATEGORIES, ...DEBT_CATEGORIES];

export function EditEstateItemModal({ isOpen, onClose, estateId, item }: EditEstateItemModalProps) {
  const { updateItem, deleteItem, isDeletingItem } = useEstateItems(estateId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [itemName, setItemName] = useState("");
  const [category, setCategory] = useState("");
  const [itemType, setItemType] = useState("");
  const [institution, setInstitution] = useState("");
  const [value, setValue] = useState("");
  const [balanceUnknown, setBalanceUnknown] = useState(false);
  const [estimateRange, setEstimateRange] = useState("");

  const INSTITUTION_CATEGORIES = new Set([
    "Bank accounts",
    "Brokerage accounts",
    "Tax-advantaged savings",
    "Retirement accounts",
    "Unsecured Debts",
  ]);

  useEffect(() => {
    if (isOpen && item) {
      setItemName(item.item_name);
      setCategory(item.category);
      setItemType(item.item_type || "");
      setInstitution((item.metadata?.institution as string) || "");
      const wasEstimated = item.is_estimated === true;
      setBalanceUnknown(wasEstimated);
      if (wasEstimated && item.balance_estimate_range) {
        setEstimateRange(item.balance_estimate_range);
        setValue("");
      } else {
        setEstimateRange("");
        setValue(item.balance.toString());
      }
    }
  }, [isOpen, item]);

  useEffect(() => {
    if (!isOpen) {
      setItemName("");
      setCategory("");
      setItemType("");
      setInstitution("");
      setValue("");
      setBalanceUnknown(false);
      setEstimateRange("");
    }
  }, [isOpen]);

  const classification: "asset" | "debt" = ASSET_CATEGORIES.includes(
    category as (typeof ASSET_CATEGORIES)[number],
  )
    ? "asset"
    : "debt";

  const categoryLabel = classification === "debt" ? "Debt category" : "Category";
  const itemTypes = category ? ACCOUNT_TYPES[category] || [] : [];

  const isPlaidConnected = !!item.plaid_connection_id;
  const isDebt = item.classification === "debt";
  const hasValue =
    isDebt ||
    isPlaidConnected ||
    !!item.value_label ||
    (balanceUnknown ? !!estimateRange : !!(value && parseFloat(value) > 0));
  const canSubmit = itemName.trim() && category && hasValue;

  const { handleChange: handleValueChange, displayValue } = currencyInput(value, setValue);

  const handleDeleteConfirm = async () => {
    try {
      await deleteItem(item.id);
      enqueueSnackbar("Account deleted", { variant: "success" });
      setShowDeleteConfirm(false);
      onClose();
    } catch {
      enqueueSnackbar("Couldn't delete account. Please try again.", { variant: "error" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const updateData: Partial<CreateEstateItemRequest> = {
        classification,
        category,
        item_type: itemType || undefined,
        item_name: itemName,
        ...(itemName !== item.item_name ? { is_custom_name: true } : {}),
        is_estimated: balanceUnknown,
        balance_estimate_range: balanceUnknown ? estimateRange : null,
      };

      if (!isPlaidConnected) {
        updateData.balance = resolveBalance(balanceUnknown, estimateRange, value);
        if (item.value_label) {
          updateData.value_label = null;
        }
      }

      const existing = (item.metadata ?? {}) as Record<string, unknown>;
      const vehicleParsed = category === "Vehicles" ? parseVehicleMetadata(itemName) : undefined;
      const institutionUpdate = institution.trim() ? { institution: institution.trim() } : {};
      if (vehicleParsed || institution.trim()) {
        updateData.metadata = { ...existing, ...vehicleParsed, ...institutionUpdate };
      }

      await updateItem(item.id!, updateData);

      enqueueSnackbar("Account updated", { variant: "success" });
      onClose();
    } catch {
      enqueueSnackbar("Couldn't update account. Please try again.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Edit</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm">{getNameLabel(category)}</Label>
              <Input
                placeholder={getPlaceholder(category)}
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm">{categoryLabel}</Label>
              <Select
                value={category}
                onValueChange={(val) => {
                  setCategory(val);
                  setItemType("");
                  setInstitution("");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {allCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {category && INSTITUTION_CATEGORIES.has(category) && (
              <div className="space-y-2">
                <Label className="text-sm">
                  Institution <span className="text-secondary">(optional)</span>
                </Label>
                <Input
                  placeholder="e.g., Chase, Fidelity, Vanguard"
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                />
              </div>
            )}

            {category && itemTypes.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">{getTypeLabel(category)}</Label>
                <Select value={itemType} onValueChange={setItemType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {itemTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!isPlaidConnected && category && (itemTypes.length === 0 || itemType) && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm">{getValueLabel(category)}</Label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-sm text-secondary">
                      $
                    </span>
                    <Input
                      type="text"
                      placeholder="0.00"
                      value={displayValue}
                      onChange={handleValueChange}
                      className="pl-7 disabled:bg-subtle disabled:text-tertiary"
                      disabled={balanceUnknown}
                    />
                  </div>
                </div>

                <BalanceEstimateSection
                  balanceUnknown={balanceUnknown}
                  estimateRange={estimateRange}
                  onBalanceUnknownChange={(checked) => {
                    setBalanceUnknown(checked);
                    if (!checked) {
                      setEstimateRange("");
                    } else {
                      setValue("");
                    }
                  }}
                  onEstimateRangeChange={setEstimateRange}
                />
              </div>
            )}
          </DialogBody>

          <div className="flex justify-between pt-6">
            <Button
              type="button"
              variant="destructive"
              disabled={isDeletingItem || isSubmitting}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </Button>
            <Button type="submit" disabled={!canSubmit || isSubmitting || isDeletingItem}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>

      <ConfirmDeleteDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Account"
        description={`Are you sure you want to delete "${item.item_name}"? This action cannot be undone.`}
        isLoading={isDeletingItem}
      />
    </Dialog>
  );
}
